var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import DateTimePicker from "react-datetime-picker";
import { defaultDateTimeFormat } from "./detail";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons";
import { convertSecondsToHHMM, parseDatetimeDefault, } from "@/components/forms/components/utils";
import moment from "moment";
import Input from "@mui/joy/Input";
import { getCurrentUserDateFormat, getCurrentUserTimeFormat, } from "@/store/utils/selectors/userSelector";
import { useAppSelector } from "@/hooks/store";
/** <b>Composant d'affichage en mode édition d'un champ de type Datetime</b>*/
export default function DateTimeFieldEdit(_a) {
    var record = _a.record, fieldName = _a.fieldName, onUpdate = _a.onUpdate, _b = _a.format, format = _b === void 0 ? defaultDateTimeFormat : _b, fieldMetadata = _a.fieldMetadata, isOnStudio = _a.isOnStudio, moduleName = _a.moduleName, isReport = _a.isReport;
    var dateFormat = useAppSelector(getCurrentUserDateFormat);
    var timeFormat = useAppSelector(getCurrentUserTimeFormat);
    var defaultFallback = "DD/MM/yyyy HH:mm";
    var dateTimeFormat = useMemo(function () {
        if (!dateFormat || !timeFormat) {
            return format || defaultFallback;
        }
        return "".concat(dateFormat, " ").concat(timeFormat);
    }, [dateFormat, timeFormat, format]);
    var pickerTokenMap = {
        d: "DD",
        m: "MM",
        Y: "yyyy",
        H: "HH",
        i: "mm",
        A: "a",
    };
    var pickerFormatString = useMemo(function () {
        return dateTimeFormat.replace(/[dmYHiA]/g, function (t) { return pickerTokenMap[t] || t; });
    }, [dateTimeFormat]);
    // Format pour moment.js (garde "A" majuscule, "YYYY" pour l'année)
    var momentTokenMap = {
        d: "DD",
        m: "MM",
        Y: "YYYY",
        H: "HH",
        i: "mm",
        A: "A",
    };
    var momentFormatString = useMemo(function () {
        return dateTimeFormat.replace(/[dmYHiA]/g, function (t) { return momentTokenMap[t] || t; });
    }, [dateTimeFormat]);
    var formatDate = function (dateString) {
        return new Date(dateString.replace(" ", "T"));
    };
    /**
     * Valuer par défaut du champ
     */
    var _c = useState(), defaultValue = _c[0], setDefaultValue = _c[1];
    useEffect(function () {
        var r = record || {};
        var ddFormat = dateTimeFormat.split("", 1);
        var timer = dateTimeFormat.split(" ", 2);
        var reFormat = "DD/MM/YYYY " + timer[1];
        var saveDate = r[fieldName];
        if (ddFormat[0] === "d" || ddFormat[0] === "D") {
            saveDate = isReport && r.value;
            var dateMomentObject = saveDate === ""
                ? moment(new Date(), reFormat)
                : moment(saveDate, reFormat);
            var dateObject = dateMomentObject.toDate();
            var initialValue = dateObject
                ? dateObject
                : (fieldMetadata.display_default &&
                    new Date(parseDatetimeDefault(fieldMetadata === null || fieldMetadata === void 0 ? void 0 : fieldMetadata.display_default))) ||
                    new Date();
            isNaN(initialValue.getTime())
                ? setDefaultValue(new Date())
                : setDefaultValue(initialValue);
        }
        else {
            saveDate = isReport && formatDate(r.value);
            var dateFormatted = isReport && r.value
                ? formatDate(r.value)
                : moment(r[fieldName], momentFormatString).toDate();
            var initialValue = fieldName && r[fieldName]
                ? dateFormatted
                : (fieldMetadata.display_default &&
                    new Date(parseDatetimeDefault(fieldMetadata === null || fieldMetadata === void 0 ? void 0 : fieldMetadata.display_default))) ||
                    new Date();
            isNaN(initialValue.getTime())
                ? setDefaultValue(new Date())
                : setDefaultValue(initialValue);
        }
    }, [record, fieldMetadata]);
    var _d = useState(defaultValue), value = _d[0], setValue = _d[1];
    useEffect(function () {
        setValue(defaultValue);
    }, [defaultValue]);
    var _e = useState((record["duration"] && record["duration"]) || ""), duration = _e[0], setDuration = _e[1];
    var onDatetimeUpdate = useCallback(function (dateTime) {
        var _a, _b;
        if (!onUpdate)
            return;
        if (dateTime) {
            var formattedForDatabase = moment(dateTime).format(momentFormatString);
            //UPDATE DURATION APRES EDITION DATE_END PAR RAPPORT A LA DATE_START
            if (moduleName === "Meetings") {
                if (fieldName === "date_end") {
                    if (!isReport) {
                        var dateStart = moment(record["date_start"], momentFormatString);
                        var dateEnd = moment(dateTime, momentFormatString);
                        var differenceInSeconds = dateEnd.diff(dateStart, "seconds");
                        setDuration(differenceInSeconds.toString());
                        var d = convertSecondsToHHMM(dateEnd.diff(dateStart, "seconds"));
                        record["duration"] = dateEnd
                            .diff(dateStart, "seconds")
                            .toString();
                        record["duration_hours"] = d.split(":")[0];
                        record["duration_minutes"] = d.split(":")[1];
                    }
                    //UPDATE DATE_END APRES EDITION DATE_START PAR RAPPORT A LA DURATION
                }
                else if (fieldName === "date_start") {
                    if (!isReport) {
                        var dateStart = moment(dateTime, momentFormatString);
                        var duration_hours = record["duration_hours"];
                        var duration_minutes = record["duration_minutes"];
                        var duration_1 = Number(duration_hours) * 3600 + Number(duration_minutes) * 60;
                        var dateEnd = dateStart.clone().add(duration_1, "seconds");
                        record["date_end"] = dateEnd.format(momentFormatString);
                    }
                }
            }
            onUpdate &&
                onUpdate((_a = {},
                    _a[fieldName] = formattedForDatabase,
                    _a.record = record,
                    _a));
            if (isReport) {
                setDefaultValue(formattedForDatabase);
            }
        }
        else {
            onUpdate((_b = {}, _b[fieldName] = null, _b));
        }
    }, [fieldName, onUpdate, momentFormatString, record, moduleName, isReport]);
    return (_jsx("div", __assign({ className: " " }, { children: isOnStudio ? (_jsx(Input, { type: "text", className: "w-full" })) : (_jsx(DateTimePicker, { className: "ml-1 z-[100]  !border-[var(--colors-input-border)] !border rounded-md !bg-[var(--colors-input-bg)] !text-[var(--colors-input-text)] ", onChange: onDatetimeUpdate, value: value, format: pickerFormatString.replace("DD", "dd"), calendarIcon: _jsx(FontAwesomeIcon, { icon: faCalendarDays }) })) })));
}
